import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { ChevronDown } from "lucide-react";
import useClickOutside from "@/hooks/useClickOutSide";
import { ShimmerLoader } from "../shimmer/Shimmer";
import { useCurrencyList } from "@/services/public.services";

interface Props {
  defaultValue?: any;
  setSelected: (option: any) => void;
  onChangeCallback: () => void;
  select: any;
  className?: string;
  selectedText: string;
  disabled: boolean;
}

const CurrencySelectBox: React.FC<Props> = ({ defaultValue, setSelected, onChangeCallback, select, className = "", selectedText, disabled }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [query, setQuery] = useState("");

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    if (query) setQuery("");
  }, [query]);

  const containerRef = useClickOutside(handleCloseDropdown);
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const { data: options, isLoading } = useCurrencyList();

  const handleSelect = useCallback(
    (option: any) => {
      setQuery("");
      setSelected(option);
      setIsDropdownOpen(false);
      if (option?.cid !== select?.cid) {
        onChangeCallback();
      }
    },
    [setSelected, select]
  );

  useEffect(() => {
    if (inputRef.current && measureRef.current) {
      if (isDropdownOpen) {
        inputRef.current.style.width = "100%";
        inputRef.current.focus();
      } else {
        // actual width of the input text content
        const textWidth = measureRef.current.offsetWidth;
        // console.log({ textWidth, M: measureRef.current });
        inputRef.current.style.width = `${textWidth - 6}px`;
      }
    }
  }, [selectedText, isDropdownOpen, isLoading]);

  useEffect(() => {
    if (defaultValue && options?.length > 0) {
      const option = options.find((o: any) => o.cid === defaultValue);
      setQuery("");
      setSelected(option);
      setIsDropdownOpen(false);
    }
  }, [defaultValue, options]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    if (!isDropdownOpen) setIsDropdownOpen(true);

    const queryLower = query.toLowerCase();

    return options.filter((option: any) => option.currency_name.toLowerCase().includes(queryLower));
  }, [isDropdownOpen, options, query]);

  const renderOption = useCallback(
    (option: any, i: number, isSelected: boolean = false) => (
      <li
        key={`${option.iso_name}${i}`}
        className={`hover:bg-brand-light ${
          isSelected ? "bg-brand-light" : ""
        } hover:text-primary text-text-default text-start hover:cursor-pointer p-1.5 text-13 gap-2 items-center`}
        onClick={() => handleSelect(option)}
      >
        <div className="overflow-hidden line-clamp-2">
          <span className="font-bold whitespace-nowrap">{`${option.currency_name} (${option.currency_symbol})`}</span>{" "}
        </div>
      </li>
    ),
    [handleSelect]
  );

  if (isLoading) return <ShimmerLoader className="w-24 h-5" />;

  return (
    <div className={`select-none transition-all duration-100 ease-in-out ${isDropdownOpen ? "w-[120px]" : ""} ${className}`} ref={containerRef}>
      <div className="flex gap-1.5 items-center border-r-2 pl-2" onClick={() => !disabled && setIsDropdownOpen((prev) => !prev)}>
        <div className=" flex items-center w-full">
          <input
            type="text"
            ref={inputRef}
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            disabled={disabled}
            placeholder={isDropdownOpen ? "Search..." : selectedText}
            className={`w-full text-nowrap disabled:cursor-not-allowed text-13 outline-none placeholder:text-text-default`}
          />
          <ChevronDown className={`cursor-pointer text-gray-500 bg-transparent ${isDropdownOpen ? "rotate-180" : ""}`} height={20} width={20} />
        </div>
      </div>
      <span
        ref={measureRef}
        className="absolute left-0 top-0 invisible whitespace-pre text-13"
        style={{
          font: "inherit",
          fontSize: "inherit",
          lineHeight: "inherit",
          paddingLeft: "inherit",
          paddingRight: "inherit",
        }}
      >
        {selectedText}
      </span>

      {isDropdownOpen && (
        <div className="w-full mt-2.5 flex items-center border border-t-0 max-h-[200px] max-w-[120px] flex-col absolute bg-white z-50 shadow-2xl">
          <ul className="flex flex-col justify-start w-full py-1 pb-2 overflow-auto h-fit tablet:py-2 tablet:pb-3">
            {select && renderOption(select, 0, true)}
            {select && <li className="border-b border-gray-200 my-1"></li>}
            {filteredOptions?.length > 0 ? (
              filteredOptions.filter((c: any) => c.currency_name !== select.currency_name).map((option: any, i: number) => renderOption(option, i))
            ) : query ? (
              <p className="text-text-error text-center text-xs py-1">Result not found!</p>
            ) : filteredOptions.length === 0 ? (
              <span className="text-13 text-center">Loading</span>
            ) : (
              filteredOptions.filter((c: any) => c.currency_name !== select.currency_name).map((option: any, i: number) => renderOption(option, i))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default React.memo(CurrencySelectBox);
